import {
  Badge,
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Spinner,
  Table,
  Tbody,
  Td,
  Textarea,
  Th,
  Thead,
  Tr,
  Text,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { useCallback, useState } from "react";
import TrackingPageLayout from "components/TrackingPageLayout";
import {
  useGuardIncidents,
  useGuardSites,
  useGuardIncidentTypes,
  createGuardIncidentMutation,
  incidentTakeOverMutation,
  incidentResolveMutation,
  incidentRejectMutation,
} from "hooks/useGuard";
import { useAuthentication } from "hooks/useAuthentication";
import { showToast, toastTypes } from "utils";

const SEVERITY_OPTIONS = [
  { value: "low", label: "Faible" },
  { value: "medium", label: "Moyen" },
  { value: "critical", label: "Critique" },
];

function formatDate(s: string | null | undefined) {
  if (!s) return "—";
  return new Date(s).toLocaleString("fr-FR");
}

export default function TrackingGuardIncidentsPage() {
  const toast = useToast();
  const { user } = useAuthentication();
  const { incidents, isLoading, error, refetch } = useGuardIncidents();
  const { sites } = useGuardSites();
  const { incidentTypes } = useGuardIncidentTypes();

  const createModal = useDisclosure();
  const resolveModal = useDisclosure();
  const [submitting, setSubmitting] = useState(false);
  const [actionId, setActionId] = useState<string | null>(null);

  const [siteId, setSiteId] = useState("");
  const [incidentTypeId, setIncidentTypeId] = useState("");
  const [severity, setSeverity] = useState("medium");
  const [description, setDescription] = useState("");

  const [resolveIncidentId, setResolveIncidentId] = useState<string | null>(null);
  const [resolutionComment, setResolutionComment] = useState("");

  const resetCreateForm = useCallback(() => {
    setSiteId("");
    setIncidentTypeId("");
    setSeverity("medium");
    setDescription("");
  }, []);

  const handleCloseCreate = useCallback(() => {
    resetCreateForm();
    createModal.onClose();
  }, [createModal, resetCreateForm]);

  const handleCreate = useCallback(async () => {
    if (!siteId || !severity) {
      showToast({ toast, message: "Site et gravité sont requis.", status: toastTypes.error });
      return;
    }
    setSubmitting(true);
    try {
      await createGuardIncidentMutation(user?.auth?.access_token, {
        site_id: siteId,
        incident_type_id: incidentTypeId || undefined,
        severity,
        description: description || undefined,
      });
      showToast({ toast, message: "Incident déclaré avec succès.", status: toastTypes.success });
      refetch();
      handleCloseCreate();
    } catch (e: any) {
      const msg = e?.response?.data?.detail || e?.message || "Erreur lors de la déclaration.";
      showToast({ toast, message: msg, status: toastTypes.error });
    } finally {
      setSubmitting(false);
    }
  }, [siteId, incidentTypeId, severity, description, user?.auth?.access_token, toast, refetch, handleCloseCreate]);

  const handleTakeOver = useCallback(async (id: string) => {
    setActionId(id);
    try {
      await incidentTakeOverMutation(user?.auth?.access_token, id);
      showToast({ toast, message: "Incident pris en charge.", status: toastTypes.success });
      refetch();
    } catch (e: any) {
      const msg = e?.response?.data?.detail || e?.message || "Erreur.";
      showToast({ toast, message: msg, status: toastTypes.error });
    } finally {
      setActionId(null);
    }
  }, [user?.auth?.access_token, toast, refetch]);

  const handleOpenResolve = useCallback((id: string) => {
    setResolveIncidentId(id);
    setResolutionComment("");
    resolveModal.onOpen();
  }, [resolveModal]);

  const handleResolve = useCallback(async () => {
    if (!resolveIncidentId) return;
    setSubmitting(true);
    try {
      await incidentResolveMutation(user?.auth?.access_token, resolveIncidentId, {
        resolution_comment: resolutionComment || undefined,
      });
      showToast({ toast, message: "Incident résolu.", status: toastTypes.success });
      refetch();
      setResolveIncidentId(null);
      setResolutionComment("");
      resolveModal.onClose();
    } catch (e: any) {
      const msg = e?.response?.data?.detail || e?.message || "Erreur.";
      showToast({ toast, message: msg, status: toastTypes.error });
    } finally {
      setSubmitting(false);
    }
  }, [resolveIncidentId, resolutionComment, user?.auth?.access_token, toast, refetch, resolveModal]);

  const handleReject = useCallback(async (id: string) => {
    setActionId(id);
    try {
      await incidentRejectMutation(user?.auth?.access_token, id);
      showToast({ toast, message: "Incident rejeté.", status: toastTypes.success });
      refetch();
    } catch (e: any) {
      const msg = e?.response?.data?.detail || e?.message || "Erreur.";
      showToast({ toast, message: msg, status: toastTypes.error });
    } finally {
      setActionId(null);
    }
  }, [user?.auth?.access_token, toast, refetch]);

  const statusOpen = (s: string | undefined) => (s || "").toLowerCase() === "open";
  const statusInProgress = (s: string | undefined) => (s || "").toLowerCase() === "in_progress";

  return (
    <TrackingPageLayout
      title="Incidents"
      action={
        <Button
          size="sm"
          colorScheme="red_origin"
          variant="outline"
          fontWeight={700}
          borderRadius="8px"
          onClick={createModal.onOpen}
        >
          Déclarer un incident
        </Button>
      }
    >
      {error && (
        <Text color="red.600" mb={3} px={4} pt={4}>
          Erreur lors du chargement des incidents.
        </Text>
      )}
      {isLoading ? (
        <Flex justify="center" py={8}><Spinner /></Flex>
      ) : (incidents?.length ?? 0) === 0 ? (
        <Box p={8} textAlign="center"><Text color="gray.600">Aucun incident.</Text></Box>
      ) : (
        <Box overflowX="auto" px={4} pb={4}>
          <Table size="sm">
            <Thead>
              <Tr>
                <Th>Type</Th>
                <Th>Site</Th>
                <Th>Gravité</Th>
                <Th>Statut</Th>
                <Th>Déclaré le</Th>
                <Th>Actions</Th>
              </Tr>
            </Thead>
            <Tbody>
              {incidents.map((inc, idx) => {
                const incAny = inc as any;
                const status = incAny.status ?? "";
                const canTakeOver = statusOpen(status);
                const canResolveOrReject = statusOpen(status) || statusInProgress(status);
                return (
                  <Tr key={inc.id ?? idx}>
                    <Td>{incAny.incident_type_name ?? "—"}</Td>
                    <Td>{incAny.site_name ?? "—"}</Td>
                    <Td>
                      <Badge colorScheme={incAny.severity === "critical" ? "red" : incAny.severity === "medium" ? "orange" : "gray"}>
                        {incAny.severity ?? "—"}
                      </Badge>
                    </Td>
                    <Td>
                      <Badge>{status || "—"}</Badge>
                    </Td>
                    <Td>{formatDate(incAny.declared_at)}</Td>
                    <Td>
                      {canResolveOrReject && (
                        <HStack spacing={2} wrap="wrap">
                          {canTakeOver && (
                            <Button
                              size="xs"
                              colorScheme="blue"
                              variant="outline"
                              onClick={() => handleTakeOver(inc.id)}
                              isLoading={actionId === inc.id}
                            >
                              Prendre en charge
                            </Button>
                          )}
                          <Button
                            size="xs"
                            colorScheme="green"
                            variant="outline"
                            onClick={() => handleOpenResolve(inc.id)}
                          >
                            Résoudre
                          </Button>
                          <Button
                            size="xs"
                            colorScheme="red"
                            variant="outline"
                            onClick={() => handleReject(inc.id)}
                            isLoading={actionId === inc.id}
                          >
                            Rejeter
                          </Button>
                        </HStack>
                      )}
                    </Td>
                  </Tr>
                );
              })}
            </Tbody>
          </Table>
        </Box>
      )}

      <Modal isOpen={createModal.isOpen} onClose={handleCloseCreate} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Déclarer un incident</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Flex flexDirection="column" gap={4}>
              <FormControl isRequired>
                <FormLabel>Site</FormLabel>
                <Select
                  placeholder="Sélectionner un site"
                  value={siteId}
                  onChange={(e) => setSiteId(e.target.value)}
                >
                  {sites.map((s) => (
                    <option key={String(s.id)} value={String(s.id)}>{s.name}</option>
                  ))}
                </Select>
              </FormControl>
              <FormControl>
                <FormLabel>Type d&apos;incident</FormLabel>
                <Select
                  placeholder="Optionnel"
                  value={incidentTypeId}
                  onChange={(e) => setIncidentTypeId(e.target.value)}
                >
                  {incidentTypes.filter((t) => t.is_active).map((t) => (
                    <option key={t.id} value={t.id}>{t.name || t.code}</option>
                  ))}
                </Select>
              </FormControl>
              <FormControl isRequired>
                <FormLabel>Gravité</FormLabel>
                <Select value={severity} onChange={(e) => setSeverity(e.target.value)}>
                  {SEVERITY_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </Select>
              </FormControl>
              <FormControl>
                <FormLabel>Description</FormLabel>
                <Textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Optionnel"
                  rows={3}
                />
              </FormControl>
            </Flex>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={handleCloseCreate}>
              Annuler
            </Button>
            <Button
              colorScheme="red_origin"
              onClick={handleCreate}
              isLoading={submitting}
            >
              Déclarer
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={resolveModal.isOpen} onClose={() => { setResolveIncidentId(null); resolveModal.onClose(); }} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Résoudre l&apos;incident</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl>
              <FormLabel>Commentaire de clôture (optionnel)</FormLabel>
              <Textarea
                value={resolutionComment}
                onChange={(e) => setResolutionComment(e.target.value)}
                placeholder="Commentaire de résolution"
                rows={3}
              />
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={() => resolveModal.onClose()}>
              Annuler
            </Button>
            <Button
              colorScheme="green"
              onClick={handleResolve}
              isLoading={submitting}
            >
              Résoudre
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </TrackingPageLayout>
  );
}
